import type { ReactNode } from 'react'
import { EntityListPage, type CardConfig } from '@/shared/components/entities/EntityListPage'
import type { FilterField, DataTableColumn } from '@/shared/components/entities'
import type { CardTemplate } from '@/shared/hooks/useCardCustomization'
import type { AssetHolding } from '@/modules/inventory/holdings'

export interface AssetHoldingFormData {
  name: string
  asset_id: string
  party_id: string
  location_id: string
  initial_quantity: string
  uom: string
  acquired_at: string
  started_at: string
  unit_cost: string
  total_cost: string
}

interface AssetHoldingListPageProps<TFormData extends AssetHoldingFormData = AssetHoldingFormData> {
  entityType: string
  labels: any
  icon: ReactNode
  filterFields: FilterField[]
  tableColumns: DataTableColumn<AssetHolding>[]
  cardConfig: CardConfig<AssetHolding>
  enableCardCustomization?: boolean
  cardCustomizationStorageKey?: string
  defaultCardTemplate?: CardTemplate
  preferDefaultCardTemplate?: boolean
  viewModeStorageKey?: string
  defaultViewMode?: 'card' | 'table'
  preferDefaultViewMode?: boolean
  loadData: () => Promise<AssetHolding[]>
  createEntity: (data: TFormData) => Promise<any>
  updateEntity: (id: string, data: TFormData) => Promise<any>
  deleteEntity: (id: string) => Promise<void>
  applyFilters: (items: AssetHolding[], searchQuery: string, filterValues: any) => AssetHolding[]
  validateForm: (data: TFormData) => Record<string, string>
  refreshKey?: number
  renderForm: (
    formData: TFormData,
    setFormData: (data: TFormData) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: AssetHolding | null,
    mode: 'create' | 'edit' | 'view'
  ) => ReactNode
  entityToFormData?: (holding: AssetHolding) => TFormData
  emptyFormData?: () => TFormData
}

export function defaultAssetHoldingEntityToFormData(
  holding: AssetHolding
): AssetHoldingFormData {
  return {
    name: holding.name ?? '',
    asset_id: holding.asset_id,
    party_id: holding.party_id ?? '',
    location_id: holding.location_id ?? '',
    initial_quantity: holding.initial_quantity != null ? String(holding.initial_quantity) : '',
    uom: holding.uom,
    acquired_at: holding.acquired_at ?? '',
    started_at: holding.started_at ?? '',
    unit_cost: holding.unit_cost != null ? String(holding.unit_cost) : '',
    total_cost: holding.total_cost != null ? String(holding.total_cost) : '',
  }
}

export function defaultEmptyAssetHoldingFormData(): AssetHoldingFormData {
  const today = new Date().toISOString().slice(0, 10)

  return {
    name: '',
    asset_id: '',
    party_id: '',
    location_id: '',
    initial_quantity: '',
    uom: 'un',
    acquired_at: today,
    started_at: today,
    unit_cost: '',
    total_cost: '',
  }
}

export function AssetHoldingListPage<TFormData extends AssetHoldingFormData = AssetHoldingFormData>({
  entityType,
  labels,
  icon,
  filterFields,
  tableColumns,
  cardConfig,
  enableCardCustomization,
  cardCustomizationStorageKey,
  defaultCardTemplate,
  preferDefaultCardTemplate,
  viewModeStorageKey,
  defaultViewMode,
  preferDefaultViewMode,
  loadData,
  createEntity,
  updateEntity,
  deleteEntity,
  applyFilters,
  validateForm,
  renderForm,
  entityToFormData = defaultAssetHoldingEntityToFormData as (holding: AssetHolding) => TFormData,
  emptyFormData = defaultEmptyAssetHoldingFormData as () => TFormData,
  refreshKey,
}: AssetHoldingListPageProps<TFormData>) {
  return (
    <EntityListPage<AssetHolding, TFormData>
      key={refreshKey}
      config={{
        entityType,
        filterFields,
        loadData,
        createEntity,
        updateEntity,
        deleteEntity,
        applyFilters,
        validateForm,
      }}
      labels={labels}
      icon={icon}
      filterFields={filterFields}
      tableColumns={tableColumns}
      cardConfig={cardConfig}
      enableCardCustomization={enableCardCustomization}
      cardCustomizationStorageKey={cardCustomizationStorageKey}
      defaultCardTemplate={defaultCardTemplate}
      preferDefaultCardTemplate={preferDefaultCardTemplate}
      viewModeStorageKey={viewModeStorageKey}
      defaultViewMode={defaultViewMode}
      preferDefaultViewMode={preferDefaultViewMode}
      renderForm={renderForm}
      entityToFormData={entityToFormData}
      emptyFormData={emptyFormData}
    />
  )
}
